import { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { motion, AnimatePresence } from 'framer-motion'
import Layout from '@/components/layout'
import { BasicDetails } from './BasicDetails'
import { DebateBuilder } from './DebateBuilder'
import { Configuration } from './Configuration'
// import { Invites } from './Invites'
// import { Schedule } from './Schedule'
import { Button } from '@/components/ui/button'
import { RotateCcw, LoaderCircle } from 'lucide-react'
import * as z from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { useAxios } from '@/contexts/AxiosProvider'
import { useRouter } from '@/hooks/useRouter'
import { capitalizeEveryWord } from '@/utils/helper'
import { DebateFormat, TurnType, WinType } from '@db8/shared'
import { useAuth } from '@/contexts/AuthProvider'

export const formSchema = z.object({
  title: z.string().min(1, { message: 'Hall title is required.' }),
  topic: z.string().min(1, { message: 'Describe your debate topic.' }),
  isPublic: z.boolean().default(true),
  format: z.string().min(1, { message: 'Debate format is required.' }),
  affirmingTeamName: z.string().default('Affirmative'),
  dissentingTeamName: z.string().default('Dissenting'),
  winType: z.string().default(WinType.SIMPLEPOLL),
  evaluationCriteria: z.array(z.string()).max(5, 'Select up to 5 criteria').optional(),
  aiEnabled: z.boolean().default(true),
  stages: z
    .array(
      z.object({
        title: z.string(),
        duration: z.number(),
        type: z.nativeEnum(TurnType),
      }),
    )
    .optional(),
})

//  { id: 'basic', label: 'Basic Details', component: BasicDetails },
//  { id: 'config', label: 'Custom Debate', component: DebateBuilder },
//  { id: 'config', label: 'Invites', component: Invites },
//  { id: 'builder', label: 'Schedule', component: Schedule },

const DebateWizard = ({ onComplete }) => {
  const [steps, setSteps] = useState([
    { id: 'basic', label: 'Basic Details', component: BasicDetails },
    { id: 'config', label: 'Configuration', component: Configuration },
  ])
  const [currentStepIndex, setCurrentStepIndex] = useState(0)
  const [direction, setDirection] = useState(0)
  const currentStep = steps[currentStepIndex]
  const [isAIProcessing, setIsAIProcessing] = useState(false)
  const [isCreatingHall, setIsCreatingHall] = useState(false)
  const auth = useAuth()

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: '',
      topic: '',
      isPublic: true,
      format: auth.currentOrg ? DebateFormat.LincolnDouglas : DebateFormat.QuickRound,
      affirmingTeamName: '',
      dissentingTeamName: '',
      winType: WinType.BALLOT,
      aiEnabled: true,
      stages: null,
      evaluationCriteria: null,
    },
  })

  const formatValue = form.watch('format')

  useEffect(() => {
    // @ts-ignore
    if (formatValue === 'custom') {
      setSteps((prev) => {
        if (!prev.some((step) => step.id === 'builder')) {
          return [...prev, { id: 'builder', label: 'Custom Debate', component: DebateBuilder }]
        }
        return prev
      })
    } else {
      setSteps((prev) => {
        const newSteps = prev.filter((step) => step.id !== 'builder')
        if (currentStepIndex >= newSteps.length) {
          setCurrentStepIndex(newSteps.length - 1)
        }
        return newSteps
      })
      form.setValue('stages', [])
    }
  }, [formatValue, currentStepIndex])

  const winType = form.watch('winType')

  useEffect(() => {
    if (winType !== WinType.EVALUATIONPOLL) form.setValue('evaluationCriteria', [])
  }, [winType])

  const goNext = () => {
    if (currentStepIndex < steps.length - 1) {
      setDirection(1)
      setCurrentStepIndex(currentStepIndex + 1)
    } else {
      const values = form.getValues()

      values.affirmingTeamName = capitalizeEveryWord(values.affirmingTeamName || 'Affirmative')
      values.dissentingTeamName = capitalizeEveryWord(values.dissentingTeamName || 'Dissenting')

      setIsCreatingHall(true)
      onComplete && onComplete(values)
    }
  }

  const goBack = () => {
    if (currentStepIndex > 0) {
      setDirection(-1)
      setCurrentStepIndex(currentStepIndex - 1)
    }
  }

  const restart = () => {
    setDirection(0)
    setCurrentStepIndex(0)
  }

  const variants = {
    initial: (direction) => ({
      opacity: 0,
      x: direction > 0 ? 50 : -50,
    }),
    animate: { opacity: 1, x: 0 },
    exit: (direction) => ({
      opacity: 0,
      x: direction > 0 ? -50 : 50,
    }),
  }

  return (
    <FormProvider {...form}>
      <div
        className="relative border shadow-lg rounded-lg p-6 max-w-2xl w-full bg-white dark:bg-background transition-all antialiased"
        style={{ zIndex: 1 }}
      >
        {isAIProcessing && <div className="modal-ai-overlay rounded-md" />}
        <div className="flex w-full items-center justify-between">
          <div className="ml-6 text-center">
            <p className="text-sm text-gray-500">
              {steps.map((step, index) => (
                <span key={step.id}>
                  {index < currentStepIndex ? (
                    <span className="font-semibold">{step.label}</span>
                  ) : index === currentStepIndex ? (
                    <span className="font-bold text-green-500">{step.label}</span>
                  ) : (
                    <span>{step.label}</span>
                  )}
                  {index !== steps.length - 1 && ' → '}
                </span>
              ))}
            </p>
          </div>
          <Button
            onClick={restart}
            className="text-sm dark:bg-transparent hover:bg-transparent border dark:text-white underline mr-4"
          >
            <RotateCcw size="15px" />
          </Button>
        </div>

        <div className="relative mb-6">
          <AnimatePresence initial={false} custom={direction} mode="wait">
            <motion.div
              key={currentStep.id}
              custom={direction}
              variants={variants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={{ duration: 0.3 }}
              className="w-full h-full "
            >
              <motion.div layout className="flex flex-col h-full min-h-[500px]">
                <currentStep.component
                  form={form}
                  onNext={goNext}
                  setIsAIProcessing={setIsAIProcessing}
                  isAIProcessing={isAIProcessing}
                  controlBtns={
                    <div className="flex justify-between mt-auto">
                      <Button
                        type="button"
                        onClick={goBack}
                        disabled={currentStepIndex === 0}
                        variant="outline"
                        className="border dark:text-white dark:bg-transparent hover:bg-transparent"
                      >
                        Back
                      </Button>
                      <Button
                        type="submit"
                        disabled={isCreatingHall}
                        variant="outline"
                        className={`border dark:text-white dark:bg-transparent hover:bg-transparent ${
                          currentStepIndex === steps.length - 1
                            ? 'gradient-bg-affirmative text-white hover:text-white hover:opacity-80'
                            : ''
                        }`}
                      >
                        {currentStepIndex === steps.length - 1 ? (
                          isCreatingHall ? (
                            <>
                              <LoaderCircle className="animate-spin h-4 w-4 mr-2" />
                              Creating...
                            </>
                          ) : (
                            'Complete'
                          )
                        ) : (
                          'Next'
                        )}
                      </Button>
                    </div>
                  }
                />
              </motion.div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </FormProvider>
  )
}

const CreateDebate = () => {
  const axios = useAxios()
  const router = useRouter()
  const { currentOrg } = useAuth()

  const handleComplete = async (values) => {
    try {
      if (values.stages?.length === 0) delete values.stages
      if (values.evaluationCriteria?.length === 0) delete values.evaluationCriteria

      const res: any = await axios.post('/api/hall', {
        ...values,
        orgId: currentOrg?.id || null,
      })

      localStorage.setItem('created', JSON.stringify({ [res.data.hallId]: true }))
      router.push(`/hall/${res.data.hallId}`)
    } catch (error) {
      // no-op
    }
  }

  return (
    <Layout>
      <div className="relative overflow-hidden flex w-full h-full items-center justify-center p-6">
        <div className="hidden md:block lobby-header-blob-sm-desktop"></div>
        <div className="hidden md:block lobby-header-blob-desktop"></div>

        <DebateWizard onComplete={handleComplete} />
      </div>
    </Layout>
  )
}

export default CreateDebate
